import { Component, ErrorInfo, ReactNode } from 'react';
import i18next from 'i18next';
import Logger from '@/utils/logger';
import { captureError } from '@/errorTracking';

interface Props {
  children: ReactNode;
}

interface State {
  hasError: boolean;
  error: Error | null;
}


/**
 * Global error boundary component
 * Catches React errors and displays a fallback UI
*/
export class ErrorBoundary extends Component<Props, State> {

  private logger = new Logger({ tag: 'ERROR_BOUNDARY' });

  constructor(props: Props) {
    super(props);
    this.state = { hasError: false, error: null };
  }

  static getDerivedStateFromError(error: Error): State {
    return { hasError: true, error };
  }

  componentDidCatch(error: Error, errorInfo: ErrorInfo) {
    this.logger.error('ErrorBoundary caught an error', error, errorInfo);
    captureError(error, { componentStack: errorInfo.componentStack });
  }

  render() {
    if (this.state.hasError) {
      return (
        <div className="flex min-h-screen items-center justify-center p-4">
          <div className="max-w-md rounded-lg border border-border bg-background p-6 shadow-lg">
            <h1 className="mb-4 text-2xl font-bold text-foreground">
              {i18next.t('errors.boundary.title', {
                ns: 'common',
                defaultValue: 'Something went wrong',
              })}
            </h1>
            <p className="mb-4 text-muted-foreground">
              {i18next.t('errors.boundary.description', {
                ns: 'common',
                defaultValue:
                  'An unexpected error occurred. Please refresh the page or contact support if the problem persists.',
              })}
            </p>
            {this.state.error && (
              <details className="mb-4">
                <summary className="cursor-pointer text-sm font-medium text-muted-foreground">
                  {i18next.t('errors.boundary.details', {
                    ns: 'common',
                    defaultValue: 'Error details',
                  })}
                </summary>
                <pre className="mt-2 overflow-auto rounded bg-muted p-2 text-xs">
                  {this.state.error.toString()}
                </pre>
              </details>
            )}
            <button
              onClick={() => window.location.reload()}
              className="rounded-md bg-primary px-4 py-2 text-primary-foreground hover:bg-primary/90 focus:outline-none focus:ring-2 focus:ring-ring"
            >
              {i18next.t('errors.boundary.reload', {
                ns: 'common',
                defaultValue: 'Reload Page',
              })}
            </button>
          </div>
        </div>
      );
    }

    return this.props.children;
  }
}

